import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link2, Unlink, Mail, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { UserIdentity } from '@supabase/supabase-js';

export function AccountTab() {
  const { isDiscordActivity } = useAuth();
  const [linking, setLinking] = useState<'google' | 'discord' | null>(null);
  const [identities, setIdentities] = useState<UserIdentity[]>([]);

  // Fetch FRESH identities directly from Supabase (not cached session)
  const fetchIdentities = async () => {
    try {
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser();
      if (error) throw error;
      setIdentities(user?.identities || []);
    } catch (err) {
      console.error('Failed to fetch identities:', err);
    }
  };

  useEffect(() => {
    // Check for error parameters in URL (from OAuth redirect)
    // Handle both hash (#error=...) and query (?error=...) params
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const queryParams = new URLSearchParams(window.location.search);

    const errorDescription =
      hashParams.get('error_description') || queryParams.get('error_description');

    const error = hashParams.get('error') || queryParams.get('error');

    if (error || errorDescription) {
      console.error('OAuth redirect error:', error, errorDescription);

      // Clean up URL to prevent toast on refresh
      const newUrl =
        window.location.pathname +
        window.location.search
          .replace(/[?&]error[^&]*/g, '')
          .replace(/[?&]error_description[^&]*/g, '');
      window.history.replaceState(null, '', newUrl);

      if (
        errorDescription?.includes('already linked') ||
        errorDescription?.includes('already registered') ||
        errorDescription?.includes('unique constraint')
      ) {
        toast.error(
          'This account is already linked to another user. Please sign out and log in with that provider to use it.',
          { duration: 8000 }
        );
      } else {
        toast.error(errorDescription || 'Failed to link account');
      }
    }

    fetchIdentities();
  }, []);

  const hasGoogle = identities.some((i) => i.provider === 'google');
  const hasDiscord = identities.some((i) => i.provider === 'discord');

  const handleLinkGoogle = async () => {
    if (hasGoogle) {
      toast.error('Google account already linked');
      return;
    }

    setLinking('google');

    try {
      if (isDiscordActivity) {
        // Discord Activity iframe blocks popups - guide user to web version
        toast.info(
          'To link Google: Open study-saga.com → Sign in with Discord → Settings → Account → Link Google',
          { duration: 10000 }
        );
        setLinking(null);
        return;
      } else {
        // Web: use linkIdentity (redirects)
        const { error } = await supabase.auth.linkIdentity({
          provider: 'google',
          options: {
            redirectTo: window.location.href,
          },
        });

        if (error) throw error;
        // Will redirect to Google OAuth
      }
    } catch (error) {
      console.error('Link Google error:', error);
      toast.error('Failed to link Google account');
      setLinking(null);
    }
  };

  const handleLinkDiscord = async () => {
    if (hasDiscord) {
      toast.error('Discord account already linked');
      return;
    }

    setLinking('discord');

    try {
      if (isDiscordActivity) {
        // Discord Activity iframe blocks redirects - guide user to web version
        toast.info(
          'To link another Discord: Open study-saga.com → Sign in with current Discord → Settings → Account',
          { duration: 10000 }
        );
        setLinking(null);
        return;
      }

      const { error } = await supabase.auth.linkIdentity({
        provider: 'discord',
        options: {
          redirectTo: window.location.href,
          scopes: 'identify guilds',
        },
      });

      if (error) throw error;
      // Will redirect to Discord OAuth
    } catch (error) {
      console.error('Link Discord error:', error);
      toast.error('Failed to link Discord account');
      setLinking(null);
    }
  };

  const handleUnlink = async (provider: 'google' | 'discord') => {
    // Check if this is the only provider
    if (identities.length <= 1) {
      toast.error('Cannot unlink your only login method');
      return;
    }

    const identity = identities.find((i) => i.provider === provider);
    if (!identity) return;

    toast('Unlink Account?', {
      description: `You will no longer be able to sign in with ${provider === 'google' ? 'Google' : 'Discord'}.`,
      duration: 10000,
      action: {
        label: 'Unlink',
        onClick: async () => {
          try {
            const { error } = await supabase.auth.unlinkIdentity(identity);
            if (error) throw error;

            // Clear provider ID from public.users to prevent stale data
            const { error: rpcError } = await supabase.rpc('clear_provider_id', {
              p_provider: provider,
            });
            if (rpcError) {
              console.error('Failed to clear provider ID:', rpcError);
              // Don't throw - unlink succeeded, this is cleanup
            }

            toast.success(`${provider === 'google' ? 'Google' : 'Discord'} account unlinked`);
            // Refresh identities from server
            await fetchIdentities();
          } catch (error) {
            console.error('Unlink error:', error);
            const message = error instanceof Error ? error.message : 'Failed to unlink account';
            if (message.includes('at least 1 identity')) {
              toast.error('Cannot unlink - this is your only login method');
            } else {
              toast.error(message);
            }
          }
        },
      },
      cancel: {
        label: 'Cancel',
        onClick: () => {},
      },
    });
  };

  const handleDeleteAccount = () => {
    toast('Delete Account?', {
      description: 'This action cannot be undone. All your data will be permanently deleted.',
      duration: 10000,
      action: {
        label: 'Delete',
        onClick: async () => {
          try {
            const { error } = await supabase.functions.invoke('delete-account');
            if (error) throw error;

            toast.success('Account deleted');
            // Force reload/logout
            await supabase.auth.signOut();
            window.location.reload();
          } catch (error) {
            console.error('Delete account error:', error);
            toast.error('Failed to delete account');
          }
        },
      },
      cancel: {
        label: 'Cancel',
        onClick: () => {},
      },
    });
  };

  return (
    <motion.div
      key="account"
      role="tabpanel"
      id="account-panel"
      aria-labelledby="account-tab"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
      className="space-y-6"
    >
      {/* Connected Accounts */}
      <div>
        <h3 className="text-white font-bold text-lg mb-2">Connected Accounts</h3>
        <p className="text-gray-400 text-sm mb-4">
          Link multiple accounts to access Study Saga from anywhere
        </p>

        <div className="space-y-3">
          {/* Discord */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#5865F2] rounded-full flex items-center justify-center">
                  <svg width="20" height="15" viewBox="0 0 71 55" fill="white">
                    <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-medium">Discord</p>
                  {hasDiscord ? (
                    <p className="text-green-400 text-sm flex items-center gap-1">
                      <Link2 size={12} /> Connected
                    </p>
                  ) : (
                    <p className="text-gray-400 text-sm">Not connected</p>
                  )}
                </div>
              </div>
              {hasDiscord ? (
                <button
                  onClick={() => handleUnlink('discord')}
                  disabled={identities.length <= 1}
                  className="px-3 py-1.5 text-red-400 hover:bg-red-500/10 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <Unlink size={14} /> Unlink
                </button>
              ) : (
                <button
                  onClick={handleLinkDiscord}
                  disabled={linking !== null}
                  className="px-3 py-1.5 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded-lg text-sm transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  {linking === 'discord' ? (
                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Link2 size={14} />
                  )}
                  Link
                </button>
              )}
            </div>
          </div>

          {/* Google */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25C22.56 11.47 22.49 10.72 22.36 10H12V14.26H17.92C17.66 15.63 16.88 16.79 15.71 17.57V20.34H19.28C21.36 18.42 22.56 15.6 22.56 12.25Z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23C14.97 23 17.46 22.02 19.28 20.34L15.71 17.57C14.73 18.23 13.48 18.63 12 18.63C9.13 18.63 6.72 16.69 5.84 14.09H2.18V16.94C3.99 20.53 7.7 23 12 23Z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09C5.62 13.43 5.49 12.73 5.49 12C5.49 11.27 5.62 10.57 5.84 9.91V7.06H2.18C1.43 8.55 1 10.22 1 12C1 13.78 1.43 15.45 2.18 16.94L5.84 14.09Z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38C13.62 5.38 15.06 5.94 16.21 7.02L19.36 3.87C17.45 2.09 14.97 1 12 1C7.7 1 3.99 3.47 2.18 7.06L5.84 9.91C6.72 7.31 9.13 5.38 12 5.38Z"
                      fill="#EA4335"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-medium">Google</p>
                  {hasGoogle ? (
                    <p className="text-green-400 text-sm flex items-center gap-1">
                      <Link2 size={12} /> Connected
                    </p>
                  ) : (
                    <p className="text-gray-400 text-sm">Not connected</p>
                  )}
                </div>
              </div>
              {hasGoogle ? (
                <button
                  onClick={() => handleUnlink('google')}
                  disabled={identities.length <= 1}
                  className="px-3 py-1.5 text-red-400 hover:bg-red-500/10 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <Unlink size={14} /> Unlink
                </button>
              ) : (
                <button
                  onClick={handleLinkGoogle}
                  disabled={linking !== null}
                  className="px-3 py-1.5 bg-white hover:bg-gray-100 text-gray-800 rounded-lg text-sm transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  {linking === 'google' ? (
                    <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
                  ) : (
                    <Link2 size={14} />
                  )}
                  Link
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Account Info - Show emails from all providers */}
      {identities.length > 0 && (
        <div>
          <h3 className="text-white font-bold text-lg mb-2">Account Info</h3>
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 space-y-3">
            {identities.map((identity) => {
              const email = identity.identity_data?.email as string | undefined;
              if (!email) return null;

              const providerName =
                identity.provider === 'discord'
                  ? 'Discord'
                  : identity.provider === 'google'
                    ? 'Google'
                    : identity.provider;

              return (
                <div key={identity.id} className="flex items-center gap-3">
                  <Mail size={16} className="text-gray-400" />
                  <div>
                    <p className="text-gray-400 text-xs">{providerName} Email</p>
                    <p className="text-white text-sm">{email}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Info */}
      <div className="text-gray-400 text-xs bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
        <p>
          💡 <strong className="text-blue-300">Tip:</strong> Link both accounts to access Study Saga
          from Discord Activity and the web with the same progress.
        </p>
      </div>

      {/* Danger Zone */}
      <div className="pt-6 border-t border-white/10">
        <h3 className="text-red-400 font-bold text-lg mb-2 flex items-center gap-2">
          <AlertTriangle size={20} /> Danger Zone
        </h3>
        <p className="text-gray-400 text-sm mb-4">Irreversible actions related to your account</p>

        <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-white font-medium">Delete Account</p>
            <p className="text-gray-400 text-xs mt-1">
              Permanently delete your account and all data
            </p>
          </div>
          <button
            onClick={handleDeleteAccount}
            className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg text-sm transition-colors border border-red-500/20 flex items-center gap-2"
          >
            <Trash2 size={16} /> Delete
          </button>
        </div>
      </div>
    </motion.div>
  );
}
